import {
	Button,
	type ColumnDef,
	DataTable,
	FilterBuilder,
	type FilterField,
	SearchInput,
	TintedBackgroundIcon,
	useFilterBuilder,
	useUrlState,
} from "@monbits/ui";
import { Coins, ExternalLink } from "lucide-react";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import CollapseString from "src/components/collapse-string/collapse-string";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import DrawerForm from "src/components/drawer-form/drawer-form";
import PaymentStatus from "src/components/payment-status/payment-status";
import TimeLabel from "src/components/time-label/time-label";
import WithdrawalDescCard from "src/components/withdraw-desc-card/withdraw-desc-card";
import balancesQueries from "src/queries/balances-queries";
import type { MerchantBalance, Withdrawal } from "src/types";
import { cryptoTickerOptions as CRYPTO_OPTIONS } from "src/utils/options";
import { STATUS_CONFIG } from "src/utils/status-config";

// API filter values differ from display values
// value = API parameter, displayStatus = what to show in UI
const WITHDRAWAL_STATUS_MAP = [
	{ value: "pending", displayStatus: "pending" },
	{ value: "processing", displayStatus: "inProgress" },
	{ value: "completed", displayStatus: "success" },
	{ value: "failed", displayStatus: "failed" },
	{ value: "cancelled", displayStatus: "cancelled" },
] as const;

const STATUS_OPTIONS = WITHDRAWAL_STATUS_MAP.map(({ value, displayStatus }) => {
	const config = STATUS_CONFIG[displayStatus];
	return {
		value,
		label: config.label,
		icon: (
			<TintedBackgroundIcon icon={config.icon} color={config.color} size="sm" />
		),
	};
});

const FILTER_FIELDS: FilterField[] = [
	{
		id: "status",
		label: "Status",
		type: "select",
		icon: <STATUS_CONFIG.pending.icon className="h-4 w-4" />,
		options: STATUS_OPTIONS,
	},
	{
		id: "currency",
		label: "Currency",
		type: "select",
		icon: <Coins className="h-4 w-4" />,
		options: CRYPTO_OPTIONS,
	},
];

export function WithdrawalsList() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const search = searchParams.get("search") || "";

	const { filters, setFilters } = useFilterBuilder({
		fields: FILTER_FIELDS,
		searchParams,
		setSearchParams,
	});

	const listBalances = balancesQueries.listBalances();
	const [balances, setBalances] = React.useState<MerchantBalance[]>(
		listBalances.data?.pages[0] || [],
	);

	const [pagination, setPagination] = useUrlState({
		page: { type: "number", default: 1 },
		limit: { type: "number", default: 20 },
	});

	const page = pagination.page;
	const limit = pagination.limit;
	const setPage = (newPage: number) => setPagination({ page: newPage });
	const setLimit = (newLimit: number) =>
		setPagination({ limit: newLimit, page: 1 });

	// Convert FilterBuilder filters to API params
	const apiFilters = React.useMemo(() => {
		const result: {
			status?: string;
			currency?: string;
		} = {};

		for (const filter of filters) {
			if (filter.operator === "is" && filter.value) {
				if (filter.field === "status") {
					result.status = filter.value;
				} else if (filter.field === "currency") {
					result.currency = filter.value;
				}
			}
		}

		return result;
	}, [filters]);

	const paginatedWithdrawals = balancesQueries.listWithdrawalPaginated({
		search,
		status: apiFilters.status,
		currency: apiFilters.currency,
		limit,
		offset: (page - 1) * limit,
	});

	const withdrawals = paginatedWithdrawals.data?.data ?? [];
	const totalWithdrawals = paginatedWithdrawals.data?.totalCount ?? 0;

	const [openedCard, changeOpenedCard] = React.useState<Withdrawal | null>(
		null,
	);
	const [isWithdrawalDetailsDrawerOpen, setIsWithdrawalDetailsDrawerOpen] =
		React.useState(false);

	const openNotification = (title: string, description: string) => {
		toast.success(title, {
			description,
		});
	};

	const isLoadingWithdrawal =
		paginatedWithdrawals.isPending || paginatedWithdrawals.isFetching;

	React.useEffect(() => {
		setBalances(listBalances.data?.pages[0] || []);
	}, [listBalances.data]);

	const handleViewWithdrawal = React.useCallback((withdrawal: Withdrawal) => {
		changeOpenedCard(withdrawal);
		setIsWithdrawalDetailsDrawerOpen(true);
	}, []);

	const withdrawalColumns: ColumnDef<Withdrawal>[] = React.useMemo(
		() => [
			{
				id: "createdAt",
				header: "Created At",
				cell: (row) => <TimeLabel time={row.createdAt} />,
			},
			{
				id: "status",
				header: "Status",
				cell: (row) => <PaymentStatus status={row.status} />,
			},
			{
				id: "network",
				header: "Network",
				cell: (row) => {
					const balance = balances.find((item) => item.id === row.balanceId);
					return (
						<div className="flex items-center gap-2">
							<CryptoIcon
								ticker={balance?.blockchain ?? ""}
								className="h-5 w-5"
							/>
							<span className="whitespace-nowrap">
								{balance?.blockchainName ?? "loading..."}
							</span>
						</div>
					);
				},
			},
			{
				id: "amount",
				header: "Amount",
				cell: (row) => {
					const balance = balances.find((item) => item.id === row.balanceId);
					return (
						<span className="whitespace-nowrap font-mono">
							{row.amount} {balance?.ticker ?? "loading..."}
						</span>
					);
				},
			},
			{
				id: "address",
				header: "Address",
				cell: (row) => (
					<CollapseString
						text={row.recipientAddress}
						collapseAt={16}
						withPopover
					/>
				),
			},
			{
				id: "transactionHash",
				header: "Transaction Hash",
				cell: (row) =>
					row.transactionHash ? (
						<div className="flex items-center gap-2">
							<CollapseString
								text={row.transactionHash}
								collapseAt={16}
								withPopover
							/>
							{row.explorerLink && (
								<Button
									variant="ghost"
									size="icon"
									aria-label="View on blockchain explorer"
									className="h-6 w-6 flex-shrink-0"
									onClick={(e) => {
										e.stopPropagation();
										window.open(row.explorerLink, "_blank");
									}}
								>
									<ExternalLink className="h-3 w-3" />
								</Button>
							)}
						</div>
					) : (
						<span className="text-muted-foreground text-sm">-</span>
					),
			},
		],
		[balances],
	);

	const hasActiveFilters = search || filters.length > 0;

	return (
		<>
			<div className="flex gap-4 flex-wrap items-center mb-4">
				<SearchInput
					placeholder="Search withdrawals..."
					className="w-full sm:w-[300px]"
				/>
				<FilterBuilder
					fields={FILTER_FIELDS}
					filters={filters}
					onFiltersChange={setFilters}
				/>
			</div>

			<DataTable
				columns={withdrawalColumns}
				data={withdrawals}
				isLoading={isLoadingWithdrawal}
				onRowClick={handleViewWithdrawal}
				getRowId={(row) => row.uuid}
				emptyMessage={
					hasActiveFilters ? "No withdrawals found" : "No withdrawals yet"
				}
				emptyDescription={
					hasActiveFilters
						? "Try adjusting your search criteria"
						: "Withdraw funds from your balances to a saved address."
				}
				emptyAction={
					hasActiveFilters ? undefined : (
						<Button variant="outline" onClick={() => navigate("/finances")}>
							Go to balances
						</Button>
					)
				}
				pagination={
					totalWithdrawals > 0
						? {
								page,
								pageSize: limit,
								totalCount: totalWithdrawals,
								onPageChange: setPage,
								onPageSizeChange: setLimit,
							}
						: undefined
				}
			/>

			<DrawerForm
				title="Withdrawal details"
				isOpen={isWithdrawalDetailsDrawerOpen}
				onClose={() => setIsWithdrawalDetailsDrawerOpen(false)}
				width={512}
			>
				{openedCard && (
					<WithdrawalDescCard
						data={openedCard}
						balances={balances}
						openNotificationFunc={openNotification}
					/>
				)}
			</DrawerForm>
		</>
	);
}
